/** Вкладка "Цены" карточки товара — таблица наш товар × 4 магазина-
 * конкурента (миграция 048). Привязка вручную (админ вставляет ссылку
 * на товар конкурента), без авто-мэтчинга — см. план.
 *
 * Само обновление цены — GET/PUT/check-now через
 * app/api/admin/competitor_prices.py, переиспользует ту же
 * `refresh_product_prices`, что и ночной cron.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, Tags } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";

interface CompetitorPriceRow {
  store_code: string;
  store_name: string;
  source_url: string | null;
  last_price: number | null; // сумы
  last_checked_at: string | null;
}

interface Props {
  productId: number;
}

function formatSum(sum: number): string {
  return sum.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " сум";
}

export function CompetitorPricesTab({ productId }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [checkingStore, setCheckingStore] = useState<string | null>(null);

  const queryKey = ["admin-competitor-prices", productId];

  const { data: rows, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await api.get(`/products/${productId}/competitor-prices`);
      return data as CompetitorPriceRow[];
    },
  });

  useEffect(() => {
    if (!rows) return;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const r of rows) {
        if (next[r.store_code] === undefined) next[r.store_code] = r.source_url ?? "";
      }
      return next;
    });
  }, [rows]);

  const saveUrlMutation = useMutation({
    mutationFn: async ({ storeCode, url }: { storeCode: string; url: string }) => {
      await api.put(`/products/${productId}/competitor-prices/${storeCode}`, {
        source_url: url,
      });
    },
    onSuccess: () => {
      toast.success("Ссылка сохранена");
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? "Не удалось сохранить ссылку"),
  });

  const checkNowMutation = useMutation({
    mutationFn: async (storeCode: string) => {
      setCheckingStore(storeCode);
      const { data } = await api.post(
        `/products/${productId}/competitor-prices/${storeCode}/check-now`,
      );
      return data as { price: number | null; status: string; error: string | null };
    },
    onSuccess: (data) => {
      if (data.status === "ok") {
        toast.success(`${formatSum(data.price!)} — цена обновлена`);
      } else {
        toast.error(
          data.error ?? (data.status === "not_found" ? "Цена не найдена на странице" : "Ошибка"),
        );
      }
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? "Не удалось проверить цену"),
    onSettled: () => setCheckingStore(null),
  });

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Загрузка цен конкурентов…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="font-semibold flex items-center gap-2">
        <Tags className="w-4 h-4" />
        Цены конкурентов
      </h3>
      <p className="text-xs text-muted-foreground">
        Вставьте ссылку на этот же товар в магазине конкурента — цена будет
        проверяться каждую ночь автоматически. «Проверить сейчас» — сразу,
        не дожидаясь ночной проверки.
      </p>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">№</TableHead>
              <TableHead className="w-32">Магазин</TableHead>
              <TableHead>Ссылка на товар</TableHead>
              <TableHead className="w-36">Цена</TableHead>
              <TableHead className="w-40">Проверено</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(rows ?? []).map((r, i) => {
              const draft = drafts[r.store_code] ?? "";
              const dirty = draft !== (r.source_url ?? "");
              const checking = checkingStore === r.store_code;
              return (
                <TableRow key={r.store_code}>
                  <TableCell className="text-muted-foreground tabular-nums">{i + 1}</TableCell>
                  <TableCell className="font-medium">{r.store_name}</TableCell>
                  <TableCell>
                    <Input
                      placeholder="https://…"
                      value={draft}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [r.store_code]: e.target.value }))
                      }
                      onBlur={() => {
                        if (dirty) {
                          saveUrlMutation.mutate({ storeCode: r.store_code, url: draft });
                        }
                      }}
                      className={`h-8 text-xs font-mono ${dirty ? "ring-1 ring-warning" : ""}`}
                    />
                  </TableCell>
                  <TableCell className="tabular-nums text-sm">
                    {r.last_price != null ? formatSum(r.last_price) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.last_checked_at ? new Date(r.last_checked_at).toLocaleString("ru-RU") : (
                      <span>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={!r.source_url || checking}
                      title="Проверить сейчас"
                      onClick={() => checkNowMutation.mutate(r.store_code)}
                    >
                      {checking ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Badge variant="outline" className="text-[10px] font-normal">
        Проверка — каждую ночь в 06:30
      </Badge>
    </div>
  );
}
